import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useToast } from '../hooks/use-toast';
import { 
  Camera, Upload, FileText, Sparkles, Brain, AlertTriangle, 
  Shield, CheckCircle, Loader2, X, Plus, Image, Eye,
  ChevronDown, ChevronUp, Download, ClipboardCheck, Target,
  HardHat, Zap, ArrowRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AIEnhancedSafetyGenerator = ({ 
  documentType = 'jha', 
  onGenerated,
  showAsDialog = false,
  isOpen = false,
  onClose
}) => {
  const { toast } = useToast();
  const [generating, setGenerating] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [generatedDocument, setGeneratedDocument] = useState(null);
  const [expandedSteps, setExpandedSteps] = useState({});
  const [converting, setConverting] = useState(false);
  
  const [formData, setFormData] = useState({
    work_description: '',
    location: '',
    project_name: '',
    department: '',
    crew_size: '',
    equipment_used: '',
    environmental_conditions: '',
    document_type: documentType
  });

  const handleImageUpload = useCallback((e) => {
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast({ title: 'Error', description: 'Image must be less than 10MB', variant: 'destructive' });
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result.split(',')[1];
        setUploadedImages(prev => [...prev, {
          id: Date.now() + Math.random(),
          preview: event.target.result,
          base64: base64,
          name: file.name
        }]);
      };
      reader.readAsDataURL(file);
    });
  }, [toast]);

  const removeImage = (imageId) => {
    setUploadedImages(prev => prev.filter(img => img.id !== imageId));
  };

  const generateDocument = async () => {
    if (!formData.work_description.trim()) {
      toast({ title: 'Error', description: 'Please describe the work being performed', variant: 'destructive' });
      return;
    }

    setGenerating(true);
    try {
      const token = localStorage.getItem('auth_token');
      
      const requestData = {
        work_description: formData.work_description,
        location: formData.location,
        project_name: formData.project_name,
        department: formData.department,
        crew_size: formData.crew_size ? parseInt(formData.crew_size) : null,
        equipment_used: formData.equipment_used ? formData.equipment_used.split(',').map(s => s.trim()) : [],
        environmental_conditions: formData.environmental_conditions,
        images_base64: uploadedImages.map(img => img.base64),
        document_type: formData.document_type
      };
      
      const response = await axios.post(
        `${API}/api/ai-enhanced-safety/generate`,
        requestData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGeneratedDocument(response.data);
      toast({ 
        title: 'Document Generated!', 
        description: `AI analyzed ${uploadedImages.length} photos and your work description`
      });
      
      if (onGenerated) {
        onGenerated(response.data);
      }
    } catch (error) {
      console.error('Generation error:', error);
      toast({ title: 'Error', description: 'Failed to generate document', variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  const convertToFormal = async (type) => {
    if (!generatedDocument) return;
    
    setConverting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const endpoint = type === 'jha' 
        ? `/api/ai-enhanced-safety/convert-to-jha/${generatedDocument.id}`
        : `/api/ai-enhanced-safety/convert-to-ptp/${generatedDocument.id}`;
      
      const response = await axios.post(endpoint, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ 
        title: `${type.toUpperCase()} Created!`, 
        description: `Document number: ${response.data.jha_number || response.data.ptp_number}`
      });
      
      // Navigate to the created document
      window.location.href = type === 'jha' ? '/jha-builder' : '/daily-ptp';
    } catch (error) {
      toast({ title: 'Error', description: `Failed to create ${type.toUpperCase()}`, variant: 'destructive' });
    } finally {
      setConverting(false);
    }
  };

  const toggleStep = (stepIndex) => {
    setExpandedSteps(prev => ({
      ...prev,
      [stepIndex]: !prev[stepIndex]
    }));
  };

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const GeneratorContent = () => (
    <div className="space-y-6" data-testid="ai-enhanced-generator">
      {!generatedDocument ? (
        <>
          {/* Document Type Selection */}
          <div className="flex gap-2">
            <Button
              variant={formData.document_type === 'jha' ? 'default' : 'outline'}
              onClick={() => setFormData({...formData, document_type: 'jha'})}
              className="flex-1"
            >
              <FileText className="w-4 h-4 mr-2" />
              Job Hazard Analysis (JHA)
            </Button>
            <Button
              variant={formData.document_type === 'ptp' ? 'default' : 'outline'}
              onClick={() => setFormData({...formData, document_type: 'ptp'})}
              className="flex-1"
            >
              <ClipboardCheck className="w-4 h-4 mr-2" />
              Pre-Task Plan (PTP)
            </Button>
          </div>

          {/* Photo Upload Section */}
          <Card className="border-dashed border-2 border-blue-200 bg-blue-50/50">
            <CardContent className="py-6">
              <div className="text-center">
                <Camera className="w-12 h-12 mx-auto text-blue-500 mb-3" />
                <h3 className="font-semibold text-slate-900 mb-1">Upload Worksite Photos (Optional)</h3>
                <p className="text-sm text-slate-500 mb-4">
                  AI will analyze photos to identify additional hazards
                </p>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="photo-upload-input"
                  />
                  <Button variant="outline" asChild>
                    <span>
                      <Upload className="w-4 h-4 mr-2" />
                      Choose Photos
                    </span>
                  </Button>
                </label>
              </div>
              
              {/* Uploaded Images Preview */}
              {uploadedImages.length > 0 && (
                <div className="mt-4 grid grid-cols-3 md:grid-cols-5 gap-2">
                  {uploadedImages.map((img) => (
                    <div key={img.id} className="relative group">
                      <img 
                        src={img.preview} 
                        alt={img.name}
                        className="w-full h-20 object-cover rounded-lg"
                      />
                      <button
                        onClick={() => removeImage(img.id)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Work Description */}
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Work Description *</Label>
              <Textarea
                value={formData.work_description}
                onChange={(e) => setFormData({...formData, work_description: e.target.value})}
                placeholder="Describe the work being performed in detail. Include tasks, equipment, materials, and any special conditions. The more detail you provide, the better the AI analysis will be."
                className="mt-1 min-h-[120px]"
                data-testid="work-description-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Location</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  placeholder="e.g., Building A, Roof"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Project Name</Label>
                <Input
                  value={formData.project_name}
                  onChange={(e) => setFormData({...formData, project_name: e.target.value})}
                  placeholder="e.g., Main Street Renovation"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Department/Industry</Label>
                <Select 
                  value={formData.department} 
                  onValueChange={(v) => setFormData({...formData, department: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="construction">Construction</SelectItem>
                    <SelectItem value="electrical">Electrical</SelectItem>
                    <SelectItem value="plumbing">Plumbing</SelectItem>
                    <SelectItem value="hvac">HVAC</SelectItem>
                    <SelectItem value="roofing">Roofing</SelectItem>
                    <SelectItem value="demolition">Demolition</SelectItem>
                    <SelectItem value="excavation">Excavation</SelectItem>
                    <SelectItem value="manufacturing">Manufacturing</SelectItem>
                    <SelectItem value="warehouse">Warehouse</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">Crew Size</Label>
                <Input
                  type="number"
                  value={formData.crew_size}
                  onChange={(e) => setFormData({...formData, crew_size: e.target.value})}
                  placeholder="e.g., 4"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium">Equipment/Tools Used</Label>
              <Input
                value={formData.equipment_used}
                onChange={(e) => setFormData({...formData, equipment_used: e.target.value})}
                placeholder="e.g., Ladder, Drill, Saw (comma separated)"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label className="text-sm font-medium">Environmental Conditions</Label>
              <Input
                value={formData.environmental_conditions}
                onChange={(e) => setFormData({...formData, environmental_conditions: e.target.value})}
                placeholder="e.g., Outdoor, hot weather, dusty conditions"
                className="mt-1"
              />
            </div>
          </div>

          {/* Generate Button */}
          <Button 
            onClick={generateDocument}
            disabled={generating || !formData.work_description.trim()}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 h-12"
            data-testid="generate-btn"
          >
            {generating ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Analyzing {uploadedImages.length > 0 ? `${uploadedImages.length} photos and ` : ''}work description...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-2" />
                Generate AI-Enhanced {formData.document_type.toUpperCase()}
              </>
            )}
          </Button>
          
          <p className="text-xs text-slate-500 text-center">
            <Brain className="w-3 h-3 inline mr-1" />
            AI will analyze your photos and description to identify hazards, controls, and PPE requirements
          </p>
        </>
      ) : (
        /* Generated Document View */
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <Badge className="mb-2 bg-green-100 text-green-700">
                <CheckCircle className="w-3 h-3 mr-1" />
                AI Generated
              </Badge>
              <h2 className="text-xl font-bold text-slate-900">{generatedDocument.title}</h2>
              <p className="text-sm text-slate-500">
                {generatedDocument.photo_hazards_count} hazards from photos • 
                {generatedDocument.text_hazards_count} hazards from analysis
              </p>
            </div>
            <Button variant="outline" onClick={() => setGeneratedDocument(null)}>
              <Plus className="w-4 h-4 mr-2" />
              New
            </Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-3">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="py-3 text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {generatedDocument.task_steps?.length || 0}
                </div>
                <div className="text-xs text-blue-600">Task Steps</div>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="py-3 text-center">
                <div className="text-2xl font-bold text-amber-600">
                  {generatedDocument.photo_hazards_count + generatedDocument.text_hazards_count}
                </div>
                <div className="text-xs text-amber-600">Hazards Found</div>
              </CardContent>
            </Card>
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="py-3 text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {generatedDocument.ppe_summary?.length || 0}
                </div>
                <div className="text-xs text-purple-600">PPE Items</div>
              </CardContent>
            </Card>
          </div>

          {/* PPE Summary */}
          {generatedDocument.ppe_summary?.length > 0 && (
            <Card className="bg-slate-50">
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <HardHat className="w-4 h-4 text-amber-500" />
                  Required PPE
                </CardTitle>
              </CardHeader>
              <CardContent className="py-2">
                <div className="flex flex-wrap gap-2">
                  {generatedDocument.ppe_summary.map((ppe, idx) => (
                    <Badge key={idx} variant="outline" className="bg-white">
                      {ppe}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Task Steps */}
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Target className="w-4 h-4" />
              Task Steps & Hazards
            </h3>
            
            {generatedDocument.task_steps?.map((step, idx) => (
              <Card key={idx} className="border-l-4 border-l-blue-500">
                <CardContent className="py-3">
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => toggleStep(idx)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                        {step.step_number}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{step.task_description}</p>
                        <p className="text-xs text-slate-500">
                          {step.hazards?.length || 0} hazards identified
                        </p>
                      </div>
                    </div>
                    {expandedSteps[idx] ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                  
                  {expandedSteps[idx] && step.hazards?.length > 0 && (
                    <div className="mt-4 space-y-3 pl-11">
                      {step.hazards.map((hazard, hIdx) => (
                        <div key={hIdx} className={`p-3 rounded-lg border ${getSeverityColor(hazard.severity)}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4" />
                                <span className="font-medium">{hazard.hazard}</span>
                                {hazard.source === 'ai_photo' && (
                                  <Badge className="text-[10px] bg-purple-100 text-purple-700">
                                    <Camera className="w-2 h-2 mr-1" />
                                    Photo
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm mt-1">{hazard.consequences}</p>
                            </div>
                            <Badge className={getSeverityColor(hazard.severity)}>
                              {hazard.severity}
                            </Badge>
                          </div>
                          
                          {hazard.controls?.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-current/10">
                              <p className="text-xs font-medium mb-1">Controls:</p>
                              <ul className="text-xs space-y-1">
                                {hazard.controls.map((ctrl, cIdx) => (
                                  <li key={cIdx} className="flex items-start gap-1">
                                    <Shield className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                    <span>{ctrl.description}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Emergency Procedures */}
          {generatedDocument.emergency_procedures && (
            <Card className="bg-red-50 border-red-200">
              <CardHeader className="py-3">
                <CardTitle className="text-sm text-red-700 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Emergency Procedures
                </CardTitle>
              </CardHeader>
              <CardContent className="py-2">
                <p className="text-sm text-red-700">{generatedDocument.emergency_procedures}</p>
              </CardContent>
            </Card>
          )}

          {/* Convert Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button 
              onClick={() => convertToFormal('jha')}
              disabled={converting}
              className="flex-1"
            >
              {converting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Save as JHA
            </Button>
            <Button 
              onClick={() => convertToFormal('ptp')}
              disabled={converting}
              variant="outline"
              className="flex-1"
            >
              {converting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ClipboardCheck className="w-4 h-4 mr-2" />}
              Save as PTP
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  if (showAsDialog) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              AI-Enhanced Safety Document Generator
            </DialogTitle>
            <DialogDescription>
              Upload photos and describe your work - AI will generate a comprehensive JHA or PTP
            </DialogDescription>
          </DialogHeader>
          <GeneratorContent />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-600" />
          AI-Enhanced Safety Document Generator
        </CardTitle>
        <CardDescription>
          Upload photos and describe your work - AI will generate a comprehensive JHA or PTP
        </CardDescription>
      </CardHeader>
      <CardContent>
        <GeneratorContent />
      </CardContent>
    </Card>
  );
};

export default AIEnhancedSafetyGenerator;
